// Vienna city boundary
const VIENNA_BOUNDARY = {
  north: 48.3229,
  south: 48.1200,
  east: 16.5779,
  west: 16.1827
};

const VIENNA_BUFFER_KM = 3;

// Simplified Vienna polygon
const VIENNA_POLYGON = [
  [48.3229, 16.2400],
  [48.3200, 16.3000],
  [48.3000, 16.3600],
  [48.2900, 16.4200],
  [48.2600, 16.4800],
  [48.2300, 16.5400],
  [48.2000, 16.5800],
  [48.1700, 16.5600],
  [48.1450, 16.5200],
  [48.1300, 16.4800],
  [48.1200, 16.4200],
  [48.1250, 16.3500],
  [48.1400, 16.3000],
  [48.1600, 16.2600],
  [48.1900, 16.2200],
  [48.2300, 16.2000],
  [48.2700, 16.1900],
  [48.3000, 16.2000],
  [48.3229, 16.2400]
];

// Airport
const VIENNA_AIRPORT = {
  lat: 48.1103,
  lng: 16.5697,
  radius: 7
};

// Public holidays
const VIENNA_PUBLIC_HOLIDAYS = {
  2025: new Set([
    '2025-01-01','2025-01-06','2025-04-20','2025-04-21','2025-05-01',
    '2025-05-29','2025-06-09','2025-06-19','2025-08-15',
    '2025-10-26','2025-11-01','2025-12-08','2025-12-25','2025-12-26'
  ]),
  2026: new Set([
    '2026-01-01','2026-01-06','2026-04-05','2026-04-06','2026-05-01',
    '2026-05-14','2026-05-25','2026-06-04','2026-08-15',
    '2026-10-26','2026-11-01','2026-12-08','2026-12-25','2026-12-26'
  ])
};

// -------------------- HELPERS --------------------

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const isPointInPolygon = (lat, lon, polygon) => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersect =
      ((yi > lon) !== (yj > lon)) &&
      lat < ((xj - xi) * (lon - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

const toViennaDate = (timestamp) => {
  const date = new Date(timestamp);

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Vienna',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date);
  const map = Object.fromEntries(parts.map(p => [p.type, p.value]));

  return new Date(
    `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}`
  );
};

const isPublicHolidayVienna = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const key = `${yyyy}-${mm}-${dd}`;
  return VIENNA_PUBLIC_HOLIDAYS[yyyy]?.has(key) ?? false;
};

// -------------------- TARIFFS --------------------

export const VIENNA_TARIFFS = {
  DAY: {
    name: 'Tagtarif',
    baseFare: 3.8,
    ratePerKmUnder5km: 0.95,
    ratePerKmOver5km: 0.58,
    ratePerMinute: 0.58,
    applies: (date) => {
      const day = date.getDay();
      const hour = date.getHours();
      if (isPublicHolidayVienna(date)) return false;
      if (day === 0) return false;
      return hour >= 6 && hour < 23;
    }
  },

  NIGHT_SUN_HOLIDAY: {
    name: 'Nachttarif / Sonn- & Feiertag',
    baseFare: 4.3,
    ratePerKmUnder5km: 1.09,
    ratePerKmOver5km: 0.67,
    ratePerMinute: 0.67,
    applies: (date) => {
      const day = date.getDay();
      const hour = date.getHours();
      if (isPublicHolidayVienna(date)) return true;
      if (day === 0) return true;
      return hour < 6 || hour >= 23;
    }
  }
};

// -------------------- SURCHARGES --------------------

export const SURCHARGES = {
  BOOKING_FEE: 2,
  OUTSIDE_VIENNA_RETURN: 2,
  AIRPORT_SURCHARGE: 7
};

// -------------------- AIRPORT & VIENNA --------------------

export const isNearAirport = (lat, lon) => {
  return (
    calculateDistance(lat, lon, VIENNA_AIRPORT.lat, VIENNA_AIRPORT.lng) <=
    VIENNA_AIRPORT.radius
  );
};

export const isWithinVienna = (lat, lon) => {
  if (isNearAirport(lat, lon)) return false;
  return isPointInPolygon(lat, lon, VIENNA_POLYGON);
};

export const getReturnDistanceToVienna = (lat, lon) => {
  if (isWithinVienna(lat, lon)) return 0;
  let min = Infinity;
  for (const [pLat, pLon] of VIENNA_POLYGON) {
    min = Math.min(min, calculateDistance(lat, lon, pLat, pLon));
  }
  return min;
};

// -------------------- FINAL DECISION --------------------

export const getCurrentTariff = (tripStartTime) => {
  if (!tripStartTime) return VIENNA_TARIFFS.DAY;

  // ✅ ONE Vienna-local Date
  const viennaDate = toViennaDate(tripStartTime);

  // ✅ Sunday = Night tariff ALL DAY
  if (viennaDate.getDay() === 0) {
    return VIENNA_TARIFFS.NIGHT_SUN_HOLIDAY;
  }

  // ✅ Public holiday = Night tariff ALL DAY
  if (isPublicHolidayVienna(viennaDate)) {
    return VIENNA_TARIFFS.NIGHT_SUN_HOLIDAY;
  }

  const hour = viennaDate.getHours();

  // ✅ Night hours
  if (hour < 6 || hour >= 23) {
    return VIENNA_TARIFFS.NIGHT_SUN_HOLIDAY;
  }

  return VIENNA_TARIFFS.DAY;
};


// -------------------- FARE HELPERS --------------------

export const calculateDistanceFare = (km, tariff) => {
  return km <= 5
    ? km * tariff.ratePerKmUnder5km
    : 5 * tariff.ratePerKmUnder5km + (km - 5) * tariff.ratePerKmOver5km;
};

export const checkOutsideViennaSurcharge = (lat, lon) => {
  if (isNearAirport(lat, lon)) {
    return { nearAirport: true, outsideVienna: false };
  }
  const inside = isPointInPolygon(lat, lon, VIENNA_POLYGON);
  return { outsideVienna: !inside, nearAirport: false };
};
